/*
 * NEXZ — pants bundle in the cart (markup and rules in
 * snippets/nexz-cart-bundle.liquid).
 *
 * - Add: one pair per press through the same single-item add path the
 *   cart set suggestion uses (Theme.routes.cart_add_url + the
 *   CartLinesUpdateEvent contract), so the drawer and the /cart page
 *   re-render from Shopify's response and every total, discount and
 *   message is recalculated by Shopify, not here.
 * - Double taps: one request at a time per page; every add button is
 *   disabled until it settles.
 * - Motion: the saved amount counts up when it grows; the headline
 *   settles in after an add. Both are skipped under reduced motion.
 * - Measurement: impression (offer seen), click (link to a colour), add
 *   (pair added from the offer) and checkout (checkout pressed with the
 *   offer in the cart), published as Shopify custom events
 *   (Shopify.analytics.publish, readable by web pixels), as Clarity
 *   events when Clarity is already on the page, and as a `nexz:bundle`
 *   DOM event. None of them is a Shopify standard event, so add-to-cart
 *   and purchase are never counted twice.
 */
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @param {string} name @param {Record<string, unknown>} detail */
function track(name, detail) {
  try {
    window.Shopify?.analytics?.publish?.(name, detail);
  } catch (e) {
    // Analytics must never break the cart.
  }
  try {
    if (typeof window.clarity === 'function') window.clarity('event', name);
  } catch (e) {
    // Same.
  }
  document.dispatchEvent(new CustomEvent('nexz:bundle', { detail: { name, ...detail } }));
}

// Page-level state survives the drawer re-rendering this element.
const seenStates = new Set();
let busy = false;
let justAdded = false;
let lastSaved = (() => {
  try {
    return Number(sessionStorage.getItem('nexz-bundle-saved') || 0);
  } catch (e) {
    return 0;
  }
})();

/** Rewrites the number inside a formatted amount ("LE 150") to `value`. */
function withNumber(text, value) {
  return text.replace(/[\d][\d,.٫٬]*/, Math.round(value).toLocaleString('en'));
}

class NexzCartBundle extends HTMLElement {
  #observer = null;
  #mutations = null;
  #visible = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.#watchImpression();
    this.#sync();
    // The cart re-renders by morphing this element in place; re-sync when
    // Shopify's new numbers arrive.
    this.#mutations = new MutationObserver(() => this.#sync());
    this.#mutations.observe(this, { attributes: true, attributeFilter: ['data-state', 'data-saved'] });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#mutations?.disconnect();
  }

  get #detail() {
    return { state: this.dataset.state, pants: Number(this.dataset.pants), saved: Number(this.dataset.saved) / 100 };
  }

  #watchImpression() {
    if (!('IntersectionObserver' in window)) return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.#visible = entry.isIntersecting;
          if (this.#visible) this.#impression();
        }
      },
      { threshold: 0.5 }
    );
    this.#observer.observe(this);
  }

  /** One impression per offer state per page view. */
  #impression() {
    const key = `${this.dataset.state}:${this.dataset.pants}`;
    if (seenStates.has(key)) return;
    seenStates.add(key);
    track('nexz_bundle_impression', this.#detail);
  }

  #sync() {
    // The state changed while the offer was already on screen.
    if (this.#visible) this.#impression();
    const counter = /** @type {HTMLElement | null} */ (this.querySelector('[data-nexz-count]'));
    const saved = counter ? Number(counter.dataset.nexzCount) / 100 : 0;
    if (counter && saved > lastSaved && !reduceMotion()) this.#countUp(counter, lastSaved, saved);
    lastSaved = saved;
    try {
      sessionStorage.setItem('nexz-bundle-saved', String(saved));
    } catch (e) {
      // Non-fatal.
    }
    if (justAdded) {
      justAdded = false;
      const header = this.querySelector('.nexz-bundle__header');
      if (header && !reduceMotion()) {
        header.classList.remove('is-confirmed');
        // Restart the settle animation on the morphed node.
        void (/** @type {HTMLElement} */ (header).offsetWidth);
        header.classList.add('is-confirmed');
      }
    }
  }

  /** Counts the visible amount from `from` to `to`; the text is final for assistive tech. */
  #countUp(el, from, to) {
    const finalText = el.textContent || '';
    const visible = document.createElement('span');
    visible.setAttribute('aria-hidden', 'true');
    visible.className = 'nexz-bundle__amount-visible';
    const sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = finalText;
    el.replaceChildren(visible, sr);
    const duration = 520;
    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const frame = (now) => {
      const t = Math.min(1, (now - start) / duration);
      visible.textContent = withNumber(finalText, from + (to - from) * ease(t));
      if (t < 1) requestAnimationFrame(frame);
      else el.replaceChildren(finalText);
    };
    requestAnimationFrame(frame);
  }

  #onChange = (event) => {
    const select = event.target.closest?.('[data-nexz-bundle-size]');
    if (!select) return;
    select.removeAttribute('aria-invalid');
    const card = select.closest('.nexz-bundle__card');
    const error = card?.querySelector('[data-nexz-bundle-error]');
    if (error) error.hidden = true;
    card?.querySelector('.nexz-bundle__note')?.setAttribute('hidden', '');
  };

  #onClick = (event) => {
    const link = event.target.closest?.('[data-nexz-bundle-link]');
    if (link) {
      const card = link.closest('.nexz-bundle__card');
      track('nexz_bundle_click', { ...this.#detail, product_id: card?.dataset.productId });
      return;
    }
    const button = event.target.closest?.('[data-nexz-bundle-add]');
    if (button) this.#add(/** @type {HTMLButtonElement} */ (button));
  };

  /** @param {HTMLButtonElement} button */
  #add(button) {
    const card = button.closest('.nexz-bundle__card');
    const select = /** @type {HTMLSelectElement | null} */ (card?.querySelector('[data-nexz-bundle-size]'));
    const error = /** @type {HTMLElement | null} */ (card?.querySelector('[data-nexz-bundle-error]'));
    const variantId = select?.value;
    track('nexz_bundle_click', { ...this.#detail, product_id: card?.dataset.productId, action: 'add' });

    if (!variantId) {
      if (error) {
        error.hidden = false;
        error.textContent = this.dataset.sizeText || '';
      }
      select?.setAttribute('aria-invalid', 'true');
      select?.focus();
      return;
    }
    if (busy) return;
    busy = true;

    const buttons = /** @type {NodeListOf<HTMLButtonElement>} */ (document.querySelectorAll('[data-nexz-bundle-add]'));
    buttons.forEach((b) => (b.disabled = true));
    const label = button.querySelector('.nexz-bundle__add-label');
    const original = label?.textContent;
    if (label) label.textContent = this.dataset.addingText || '';
    button.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;

    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'bundle',
        lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));
    const fetchCfg = fetchConfig('javascript', { body: formData });
    const errorText = this.dataset.errorText || '';
    const detail = this.#detail;

    const fail = (message) => {
      if (error) {
        error.hidden = false;
        error.textContent = message || errorText;
      }
    };

    fetch(Theme.routes.cart_add_url, { ...fetchCfg, headers: { ...fetchCfg.headers, Accept: 'application/json' } })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          deferred.reject(new Error(response.description || errorText));
          fail(response.description);
          return;
        }
        if (label) label.textContent = this.dataset.addedText || '';
        justAdded = true;
        track('nexz_bundle_add', { ...detail, variant_id: Number(variantId) });
        const ajaxCart = await fetch(Theme.routes.cart_url + '.js').then((r) => r.json());
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { items: ajaxCart.items, itemCount: 1, source: 'nexz-cart-bundle', sections: response.sections, didError: false },
        });
      })
      .catch((err) => {
        deferred.reject(err);
        fail();
      })
      .finally(() => {
        busy = false;
        button.removeAttribute('aria-busy');
        document.querySelectorAll('[data-nexz-bundle-add]').forEach((b) => (/** @type {HTMLButtonElement} */ (b).disabled = false));
        // If the element survived (error, or no re-render), restore the label.
        if (label && label.isConnected && label.textContent !== this.dataset.addedText) label.textContent = original;
      });
  }
}

if (!customElements.get('nexz-cart-bundle')) customElements.define('nexz-cart-bundle', NexzCartBundle);

// Checkout pressed with the bundle in the cart (drawer or /cart page).
document.addEventListener(
  'click',
  (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!target.closest?.('#checkout, [name="checkout"]')) return;
    const bundle = /** @type {HTMLElement | null} */ (document.querySelector('nexz-cart-bundle'));
    if (!bundle) return;
    track('nexz_bundle_checkout', {
      state: bundle.dataset.state,
      pants: Number(bundle.dataset.pants),
      saved: Number(bundle.dataset.saved) / 100,
    });
  },
  { capture: true }
);
